// src/pages/admin/Categories.tsx
//
// Catalog category workbench: assign and modify the migration-024
// taxonomy on products and brands.
//
// Left: the live category tree (public /api/categories) — clicking a
// node makes it the assignment TARGET. Right: a product workbench with
// brand/search/mapping filters, rule-based suggestions from the seed's
// classifiers (one-click accept per row, or accept-all), row-level
// multi-select bulk assignment, and a whole-brand bulk assign that only
// touches unmapped products by default (manual curation is preserved).
//
// Admin overrides are explicit server writes; the taxonomy seed only
// ever fills NULLs, so curated values survive reseeds.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check, ChevronRight, FolderTree, Search, Sparkles, Tag, X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { useAuth } from "@/components/auth/AuthContext";
import {
  acceptTaxonomySuggestions,
  adminTaxonomyProducts,
  assignBrandCategory,
  assignProductCategory,
  fetchBrands,
  fetchCategories,
  type AdminTaxRow,
  type BrandRow,
  type TaxCategory,
} from "@/lib/api";
import { cn } from "@/lib/cn";

const PAGE_SIZE = 60;

type Mapping = "all" | "unmapped" | "mapped";

export function AdminCategories() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [tax, setTax] = useState<TaxCategory[]>([]);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [target, setTarget] = useState<TaxCategory | null>(null);
  const [mapping, setMapping] = useState<Mapping>("unmapped");
  const [brand, setBrand] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<AdminTaxRow[]>([]);
  const [total, setTotal] = useState(0);
  const [coverage, setCoverage] = useState<{ total: number; unmapped: number } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const fetchId = useRef(0);

  const bySlug = useMemo(
    () => new Map(tax.map((c) => [c.slug, c])), [tax]);
  const byId = useMemo(
    () => new Map(tax.map((c) => [c.id, c])), [tax]);

  /** "Video › Projection screens › Fixed frame" */
  const pathLabel = useCallback((slug: string | null): string => {
    if (!slug) return "—";
    const parts: string[] = [];
    let node = bySlug.get(slug);
    while (node) {
      parts.unshift(node.names.en || node.names.fr || node.slug);
      node = node.parent_id != null ? byId.get(node.parent_id) : undefined;
    }
    return parts.join(" › ");
  }, [bySlug, byId]);

  const refreshCoverage = useCallback(() => {
    Promise.all([
      adminTaxonomyProducts({ mapping: "all", limit: 1 }),
      adminTaxonomyProducts({ mapping: "unmapped", limit: 1 }),
    ])
      .then(([all, un]) => setCoverage({ total: all.total, unmapped: un.total }))
      .catch(() => setCoverage(null));
  }, []);

  const load = useCallback((offset: number, append: boolean) => {
    const id = ++fetchId.current;
    if (!append) setLoading(true);
    setError("");
    adminTaxonomyProducts({
      q: query || undefined,
      brand: brand || undefined,
      mapping,
      limit: PAGE_SIZE,
      offset,
    })
      .then((page) => {
        if (id !== fetchId.current) return;
        setRows((cur) => append ? [...cur, ...page.rows] : page.rows);
        setTotal(page.total);
      })
      .catch((e) => {
        if (id !== fetchId.current) return;
        setError(String(e));
      })
      .finally(() => {
        if (id === fetchId.current) setLoading(false);
      });
  }, [query, brand, mapping]);

  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    fetchCategories().then((j) => setTax(j.categories)).catch(() => {});
    fetchBrands().then((j) => setBrands(j.rows)).catch(() => {});
    refreshCoverage();
  }, [auth.isReady, auth.isAuthenticated, refreshCoverage]);

  // debounce search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setSelected(new Set());
    load(0, false);
  }, [load]);

  const suggestedCount = useMemo(
    () => rows.filter((r) => r.suggested_slug).length, [rows]);

  function patchRow(productId: number, subcategoryId: number | null,
                    slug: string | null) {
    setRows((cur) => cur.map((r) => r.product_id === productId
      ? { ...r, subcategory_id: subcategoryId, subcategory_slug: slug,
          suggested_slug: null }
      : r));
  }

  async function assignOne(row: AdminTaxRow, subcategoryId: number | null,
                           slug: string | null) {
    setBusy(true);
    setError("");
    try {
      await assignProductCategory(row.product_id, subcategoryId);
      patchRow(row.product_id, subcategoryId, slug);
      if (mapping === "unmapped" && subcategoryId != null) {
        setRows((cur) => cur.filter((r) => r.product_id !== row.product_id));
        setTotal((t) => t - 1);
      }
      refreshCoverage();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function bulkAssign(subcategoryId: number | null) {
    if (selected.size === 0) return;
    setBusy(true);
    setError("");
    try {
      await Promise.all([...selected].map((id) =>
        assignProductCategory(id, subcategoryId)));
      setNotice(
        subcategoryId == null
          ? t("admin.categories.bulkCleared", { count: selected.size })
          : t("admin.categories.bulkAssigned", {
              count: selected.size,
              target: target?.names.en ?? target?.slug,
            }),
      );
      setSelected(new Set());
      load(0, false);
      refreshCoverage();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function brandAssign() {
    const row = brands.find((b) => b.name === brand);
    if (!row || row.brand_id == null || !target) return;
    setBusy(true);
    setError("");
    try {
      const res = await assignBrandCategory(row.brand_id, target.id, true);
      setNotice(
        t("admin.categories.brandAssigned", {
          count: res.affected,
          brand,
          target: target.names.en ?? target.slug,
        }),
      );
      load(0, false);
      refreshCoverage();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function acceptAll() {
    setBusy(true);
    setError("");
    try {
      const res = await acceptTaxonomySuggestions(brand || undefined);
      setNotice(
        t("admin.categories.suggestionsApplied", {
          mapped: res.mapped,
          skipped: res.skipped,
        }),
      );
      load(0, false);
      refreshCoverage();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  function toggleSelect(id: number) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function renderTree(parentId: number | null, depth: number) {
    return tax
      .filter((c) => c.parent_id === parentId)
      .map((c) => (
        <div key={c.id}>
          <button
            type="button"
            onClick={() => setTarget((cur) => cur?.id === c.id ? null : c)}
            className={cn(
              "flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-sm hover:bg-bg-hover",
              target?.id === c.id && "bg-accent-indigo/15 text-accent-indigo ring-1 ring-accent-indigo",
            )}
            style={{ paddingLeft: `${8 + depth * 16}px` }}
          >
            {tax.some((k) => k.parent_id === c.id)
              ? <ChevronRight className="h-3 w-3 rotate-90 text-fg-tertiary" />
              : <span className="w-3" />}
            <span className="flex-1 truncate">
              {c.names.en || c.names.fr || c.slug}
            </span>
            <span className="text-xs text-fg-tertiary">{c.product_count}</span>
          </button>
          {renderTree(c.id, depth + 1)}
        </div>
      ));
  }

  const coveragePct = coverage && coverage.total > 0
    ? Math.round(100 * (coverage.total - coverage.unmapped) / coverage.total)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <FolderTree className="h-5 w-5 text-accent-indigo" />
        <h1 className="text-lg font-semibold">{t("admin.categories.title")}</h1>
        {coveragePct != null && coverage && (
          <div className="flex items-center gap-2 text-xs text-fg-tertiary">
            <div className="h-1.5 w-40 overflow-hidden rounded bg-bg-elevated">
              <div className="h-full rounded bg-accent-indigo"
                   style={{ width: `${coveragePct}%` }} />
            </div>
            {t("admin.categories.coverage", {
              mapped: coverage.total - coverage.unmapped,
              total: coverage.total,
              pct: coveragePct,
            })}
          </div>
        )}
      </div>

      {notice && (
        <div className="flex items-center gap-2 rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400">
          <Check className="h-4 w-4" /> {notice}
          <button type="button" className="ml-auto" onClick={() => setNotice("")}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {error && (
        <div className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* ------- category tree = assignment target ------- */}
        <Card className="lg:col-span-1">
          <CardContent className="p-3">
            <div className="mb-2 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.categories.targetCategory")}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">{renderTree(null, 0)}</div>
            <div className="mt-2 border-t border-border pt-2 text-xs text-fg-tertiary">
              {target
                ? <>{t("admin.categories.target")}<span className="text-accent-indigo">{pathLabel(target.slug)}</span></>
                : t("admin.categories.targetHint")}
            </div>
          </CardContent>
        </Card>

        {/* ------- product workbench ------- */}
        <Card className="lg:col-span-2">
          <CardContent className="space-y-3 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-fg-tertiary" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t("admin.categories.searchPlaceholder")}
                  className="h-9 w-56 pl-7"
                />
              </div>
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="h-9 rounded border border-border bg-bg-surface px-2 text-sm text-fg-primary"
              >
                <option value="">{t("admin.categories.allBrands")}</option>
                {brands.map((b) => (
                  <option key={b.brand_id ?? b.name} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
              <div className="flex overflow-hidden rounded border border-border text-sm">
                {(["all", "unmapped", "mapped"] as Mapping[]).map((mval) => (
                  <button
                    key={mval}
                    type="button"
                    onClick={() => setMapping(mval)}
                    className={cn(
                      "px-3 py-1.5",
                      mapping === mval
                        ? "bg-accent-indigo text-white"
                        : "bg-bg-surface text-fg-secondary hover:bg-bg-hover",
                    )}
                  >
                    {mval === "all"
                      ? t("admin.categories.mappingAll")
                      : mval === "unmapped"
                        ? t("admin.categories.mappingUnmapped")
                        : t("admin.categories.mappingMapped")}
                  </button>
                ))}
              </div>
              {brand && target && (
                <Button variant="secondary" disabled={busy} onClick={brandAssign}>
                  <Tag className="mr-1.5 h-3.5 w-3.5" />
                  {t("admin.categories.assignAll", {
                    brand,
                    target: target.names.en ?? target.slug,
                  })}
                </Button>
              )}
            </div>

            {mapping === "unmapped" && suggestedCount > 0 && (
              <div className="flex items-center gap-2 rounded border border-accent-indigo/30 bg-accent-indigo/10 px-3 py-2 text-sm">
                <Sparkles className="h-4 w-4 text-accent-indigo" />
                <span>
                  {t("admin.categories.suggestionCount", { count: suggestedCount })}
                </span>
                <Button variant="secondary" className="ml-auto" disabled={busy}
                        onClick={acceptAll}>
                  {t("admin.categories.acceptAllSuggestions")}
                  {brand ? t("admin.categories.acceptAllSuggestionsFor", { brand }) : ""}
                </Button>
              </div>
            )}

            {selected.size > 0 && (
              <div className="flex items-center gap-2 rounded border border-border bg-bg-elevated px-3 py-2 text-sm">
                <span>{t("admin.categories.selectedCount", { count: selected.size })}</span>
                <Button variant="secondary" disabled={!target || busy}
                        onClick={() => bulkAssign(target?.id ?? null)}>
                  {t("admin.categories.assignTo", {
                    target: target ? (target.names.en ?? target.slug) : "…",
                  })}
                </Button>
                <Button variant="ghost" disabled={busy}
                        onClick={() => bulkAssign(null)}>
                  {t("admin.categories.clearCategory")}
                </Button>
                <Button variant="ghost" onClick={() => setSelected(new Set())}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            {loading ? (
              <div className="py-8 text-center text-sm text-fg-tertiary">{t("admin.categories.loading")}</div>
            ) : rows.length === 0 ? (
              <EmptyState
                title={t(
                  mapping === "unmapped"
                    ? "admin.categories.emptyMapped"
                    : "admin.categories.emptyNoProducts",
                )}
                description={t(
                  mapping === "unmapped"
                    ? "admin.categories.emptyMappedDescription"
                    : "admin.categories.emptyNoProductsDescription",
                )}
              />
            ) : (
              <div className="divide-y divide-border">
                {rows.map((r) => (
                  <div key={r.product_id}
                       className="flex items-center gap-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selected.has(r.product_id)}
                      onChange={() => toggleSelect(r.product_id)}
                      className="h-4 w-4 accent-indigo-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-510">{r.full_name}</div>
                      <div className="text-xs text-fg-tertiary">{r.brand_raw}</div>
                    </div>
                    {r.subcategory_slug ? (
                      <Badge>{pathLabel(r.subcategory_slug)}</Badge>
                    ) : (
                      <span className="text-xs text-fg-tertiary">—</span>
                    )}
                    {r.suggested_slug && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => assignOne(
                          r, bySlug.get(r.suggested_slug!)?.id ?? null,
                          r.suggested_slug)}
                        className="inline-flex items-center gap-1 rounded border border-dashed border-accent-indigo/50 px-2 py-0.5 text-xs text-accent-indigo hover:bg-accent-indigo/10"
                        title={t("admin.categories.acceptSuggestion")}
                      >
                        <Sparkles className="h-3 w-3" />
                        {pathLabel(r.suggested_slug)}
                        <Check className="h-3 w-3" />
                      </button>
                    )}
                    {target && (
                      <Button variant="ghost" disabled={busy}
                              onClick={() => assignOne(r, target.id, target.slug)}
                              title={t("admin.categories.assignToPath", { path: pathLabel(target.slug) })}>
                        → {target.names.en ?? target.slug}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {rows.length < total && (
              <div className="pt-2 text-center">
                <Button variant="secondary" disabled={busy}
                        onClick={() => load(rows.length, true)}>
                  {t("admin.categories.loadMore", { shown: rows.length, total })}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
